import yaml from 'js-yaml';

function tryParseUrlBase(url) {
  try {
    const withProto = url.includes('://') ? url : `https://${url}`;
    const u = new URL(withProto.replace(/\{\{[^}]+\}\}/g, 'placeholder'));
    return `${u.protocol}//${u.host}`;
  } catch {
    return null;
  }
}

function pathFromUrl(url) {
  if (!url) return '/';
  const stripped = url.replace(/^https?:\/\/[^/]+/i, '');
  const pathOnly = stripped.split('?')[0] || '/';
  return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
}

function buildParameters(request) {
  const parameters = [];
  for (const h of request.headers || []) {
    if (!h.key || h.enabled === false) continue;
    parameters.push({
      name: h.key,
      in: 'header',
      schema: { type: 'string' },
      example: h.value ?? '',
    });
  }
  for (const p of request.params || []) {
    if (!p.key || p.enabled === false) continue;
    parameters.push({
      name: p.key,
      in: 'query',
      schema: { type: 'string' },
      example: p.value ?? '',
    });
  }
  return parameters;
}

function buildRequestBody(request, warnings) {
  const bodyType = request.body_type || request.bodyType;
  if (bodyType === 'formdata') {
    warnings.push(`Multipart body for "${request.name}" not represented in OpenAPI export`);
    return undefined;
  }
  if (bodyType === 'json' && request.body) {
    let example;
    try {
      example = JSON.parse(request.body);
    } catch {
      example = request.body;
    }
    return {
      content: {
        'application/json': {
          schema: { type: 'object' },
          example,
        },
      },
    };
  }
  if (bodyType === 'form' && request.body) {
    return {
      content: {
        'application/x-www-form-urlencoded': {
          schema: { type: 'object' },
        },
      },
    };
  }
  if (request.body && bodyType === 'raw') {
    return {
      content: {
        'text/plain': {
          schema: { type: 'string' },
          example: request.body,
        },
      },
    };
  }
  return undefined;
}

/**
 * @param {object} collection
 * @param {{ push: (msg: string) => void }} warnings
 * @param {{ asYaml?: boolean }} options
 */
export function toOpenApi3(collection, warnings, options = {}) {
  const paths = {};
  const servers = new Set();
  const requests = collection.requests || [];

  for (const req of requests) {
    const method = (req.method || 'get').toLowerCase();
    const pathKey = pathFromUrl(req.url || '/');
    const base = tryParseUrlBase(req.url || '');
    if (base) servers.add(base);

    if (!paths[pathKey]) paths[pathKey] = {};
    if (paths[pathKey][method]) {
      warnings.push(`Duplicate path ${method.toUpperCase()} ${pathKey}; last request wins`);
    }

    const operation = {
      summary: req.name || method.toUpperCase(),
      description: req.description || undefined,
      parameters: buildParameters(req),
      requestBody: buildRequestBody(req, warnings),
      responses: {
        default: { description: 'Response' },
      },
    };

    paths[pathKey][method] = operation;
  }

  const serverList =
    servers.size > 0
      ? [...servers].map((url) => ({ url }))
      : [{ url: 'https://api.example.com' }];

  if (servers.size === 0) {
    warnings.push('No server URL detected; using placeholder https://api.example.com');
  }

  warnings.push('OpenAPI export does not include auth flows, scripts, or collection variables as components');

  const spec = {
    openapi: '3.0.3',
    info: {
      title: collection.name || 'API',
      version: '1.0.0',
      description: collection.description || '',
    },
    servers: serverList,
    paths,
  };

  if (options.asYaml) {
    return yaml.dump(spec, { lineWidth: 120 });
  }
  return spec;
}
